import React, { FC, useCallback, useReducer, useEffect } from 'react';

import { Tabs } from 'src/common/form/tabs';
import { StepEditorContentLibraryIntegrations } from './integrations';
import { StepEditorContentLibraryAddIntegrationButton } from './add-integration-button';
import { LibraryContentList } from 'src/common/components/library/content-list';

import { LibraryTabType, LIBRARY_TABS } from './types';
import { FilterBar } from 'src/common/components/filter-bar';
import { useStepEditorContext } from 'src/store/step-editor';
import { EventBus } from 'src/common/EventBus';
import { Content } from 'src/common/interfaces/content.interface';

interface StepEditorContentLibraryProps {
  onChooseExisting: (content: Content) => void;
}

const TabLabel = ({ label }: { label: string }) => {
  return <div className='cursor-pointer'>{label}</div>;
};

export const StepEditorContentLibrary: FC<StepEditorContentLibraryProps> = ({ onChooseExisting }) => {
  const [forceUpdateCounter, forceUpdate] = useReducer((x) => x + 1, 0);
  const { libraryTab, setLibraryTab } = useStepEditorContext();

  useEffect(() => {
    return () => {
      // reset the library tab on unmount
      setLibraryTab(LIBRARY_TABS[0].type);
    };
  }, [setLibraryTab]);

  const onChangeTab = useCallback(
    (e: any, value: string) => {
      const tab = value as LibraryTabType;
      setLibraryTab(tab);
    },
    [setLibraryTab]
  );

  const onExistingContentSelected = useCallback(
    (content: Content) => {
      setTimeout(() => {
        // warning panel mispositions like library is expanded, needs to be re-rendered
        EventBus.emit('editor:render-reset-content-warning-panel');
      }, 200);
      onChooseExisting(content);
    },
    [onChooseExisting]
  );

  return (
    <div className='flex flex-1 flex-col'>
      <div className='text-bedrock-h2 text-bedrock-black'>Library</div>
      <div className='flex items-center mt-6 space-x-md divide-x divide-bedrock-gray-medium'>
        <Tabs
          theme='bedrock'
          value={libraryTab}
          onChange={onChangeTab}
          options={LIBRARY_TABS.map((tab) => ({
            value: tab.type,
            label: <TabLabel label={tab.label} />,
          }))}
        />
        {libraryTab === 'library' && <FilterBar searchPlaceholder='Search your Library' widthClassname='w-[150px]' />}
        {libraryTab === 'integrations' && (
          <div className='pl-md'>
            <StepEditorContentLibraryAddIntegrationButton onIntegrationAdded={forceUpdate} />
          </div>
        )}
      </div>
      <div className='mt-4'>
        {libraryTab === 'library' ? (
          <LibraryContentList tableClassName='w-full' onChooseExisting={onExistingContentSelected} />
        ) : libraryTab === 'integrations' ? (
          <StepEditorContentLibraryIntegrations forceUpdateCounter={forceUpdateCounter} />
        ) : null}
      </div>
    </div>
  );
};
